import isUuid from 'is-uuid';
import type { ImageSizeKeysTypes } from '@axiom/const';
import { CandidateImageConst, CandidatesConst } from '@axiom/const';
import type {
  Candidate,
  CandidateEngagementDetails,
  User,
} from '@axiom/validation';

import { EnvUtil } from './env-util';

const { ProfileStatuses } = CandidatesConst;

const { IMAGE_RESIZE_DIRECTORY, ImageSizesByWidth, PROFILE_IMAGE_DIRECTORY } =
  CandidateImageConst;

type CandidateRecordLegacy = {
  record: Candidate;
};

type CandidateType = CandidateRecordLegacy | Candidate;

export const profileStatusesToShowAxiomAnswers = [
  ProfileStatuses.Alum,
  ProfileStatuses.AlumDNR,
  ProfileStatuses.PendingAlumDNR,
  ProfileStatuses.Certifying,
  ProfileStatuses.NewLead,
  ProfileStatuses.Rejected,
  ProfileStatuses.Reservoir,
];

export const CandidateUtil = {
  getCandidateData(data: CandidateType) {
    return 'record' in data ? data.record : data;
  },

  reconcileUserCandidateId(
    user?: User,
    candidateIdFromUrl?: Candidate['id']
  ): Candidate['id'] {
    const candidateId =
      user?.roles.some(r => r.startsWith('Envoy')) &&
      candidateIdFromUrl &&
      isUuid.anyNonNil(candidateIdFromUrl)
        ? candidateIdFromUrl
        : user?.candidateId;

    if (!candidateId) {
      throw new Error('Unknown candidate id');
    }
    return candidateId;
  },

  getProfileImageUri(
    candidate: CandidateType,
    size: ImageSizeKeysTypes | null
  ) {
    const { profileImageKey } = this.getCandidateData(candidate);

    if (
      !profileImageKey ||
      !size ||
      !(size in ImageSizesByWidth) ||
      !EnvUtil.publicS3Bucket
    ) {
      return null;
    }

    const [idSlug] = profileImageKey
      .replace(`${PROFILE_IMAGE_DIRECTORY}/`, '')
      .split('/');
    const { width, height } = ImageSizesByWidth[size];

    return `${EnvUtil.publicBaseUrlS3}/${EnvUtil.publicS3Bucket}/${IMAGE_RESIZE_DIRECTORY}/${idSlug}/${width}x${height}`;
  },

  getTalentPartnerUser(
    candidate: Candidate | CandidateEngagementDetails['candidate']
  ): Pick<User, 'email' | 'firstName' | 'lastName' | 'fullName'> {
    const { ownerUser, profileStatus } = candidate;

    const axiomAnswers = {
      email: EnvUtil.axiomAnswersEmail,
      firstName: 'Axiom',
      lastName: 'Answers',
      fullName: 'Axiom Answers',
    };

    if (profileStatusesToShowAxiomAnswers.includes(profileStatus)) {
      return axiomAnswers;
    }

    return ownerUser
      ? {
          email: ownerUser.email,
          firstName: 'firstName' in ownerUser ? ownerUser.firstName : null,
          lastName: 'lastName' in ownerUser ? ownerUser.lastName : null,
          fullName: ownerUser.fullName,
        }
      : axiomAnswers;
  },
};
